/* ====== 优化合并后的 React 组件 (App.js)  ====== */
import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import './App.css';
import { renderAppStep } from './components/AppSections';
import {
  handleExtractDirectory,
  handleExtractTable,
} from './modules/carHandlers';
import {
  DEVICE_TYPES,
  handleProcessDeviceCalibration,
  handleProcessDeviceCalibrationBatch
} from './modules/deviceHandlers';
import {
  DEFAULT_FILTERS,
  DOCUMENT_TYPE,
  NO_LEVEL_KEY,
  PREDEFINED_DIRECTORIES,
  STEP,
  UPLOAD_MODE
} from './modules/workflowConfig';
import {
  cloneOriginalTables,
  applyCellEditToTables,
  processTablesByType
} from './modules/oneClickValueProcessor';
import { postFormData } from './modules/apiClient';
import {
  applyHistoryTaskResult,
  fetchHistoryTaskDetail,
  loadHistoryTasks,
} from './modules/historyTaskHandlers';
import { initialState } from './modules/appState';

export default function App() {
  const [state, dispatch] = useReducer((prevState, partial) => ({ ...prevState, ...partial }), initialState);
  const deviceOriginalTablesRef = useRef([]);
  const updateState = (partial) => dispatch(partial);
  const resetResultState = () => {
    deviceOriginalTablesRef.current = [];
    updateState({
      tables: [],
      transformedTables: [],
      currentTransformationType: 'original',
      offsetAmount: '1'
    });
  };

  const resetCarFlowState = () => updateState({
    uploadMode: UPLOAD_MODE.FULL,
    selectedSinglePageDirectory: '',
    directoryInfo: [],
    selectedDirectory: '',
    availableTables: [],
    tableHtml: '',
    selectedLevel: DEFAULT_FILTERS.selectedLevel,
    availableLevels: [],
  });

  const resetDeviceFlowState = () => updateState({ deviceType: '' });

  const resetCommon = (next = {}) => updateState({ file: null, deviceFiles: [], loading: false, error: '', ...next });

  useEffect(() => {
    if (!state.tables.length) {
      return;
    }

    updateState({
      transformedTables: cloneOriginalTables(state.tables),
      currentTransformationType: 'original'
    });
  }, [state.tables]);

    useEffect(() => {
    if (state.documentType !== DOCUMENT_TYPE.DEVICE || !state.tables.length) {
      return;
    }

    if (!deviceOriginalTablesRef.current.length) {
      deviceOriginalTablesRef.current = cloneOriginalTables(state.tables);
    }
  }, [state.documentType, state.tables]);

  useEffect(() => {
    if (!(state.documentType === DOCUMENT_TYPE.STANDARD || state.documentType === DOCUMENT_TYPE.ENTERPRISE) || state.currentStep !== STEP.RESULTS) {
      return;
    }

    const levelTables = state.carTablesByLevel?.[state.selectedLevel];
    if (Array.isArray(levelTables) && levelTables.length) {
      updateState({ tables: cloneOriginalTables(levelTables) });
    }
  }, [state.documentType, state.currentStep, state.selectedLevel, state.carTablesByLevel]);

  const isStandardMode = state.documentType === DOCUMENT_TYPE.STANDARD;
  const isEnterpriseMode = state.documentType === DOCUMENT_TYPE.ENTERPRISE;
  const isCarMode = isStandardMode || isEnterpriseMode;
  const isDeviceMode = state.documentType === DOCUMENT_TYPE.DEVICE;
  const isSingleCarMode = isCarMode && state.uploadMode === UPLOAD_MODE.SINGLE;

  const availableLevels = useMemo(() => {
    const levelEntries = Object.entries(state.carTablesByLevel || {});
    const levelsWithRows = levelEntries
      .filter(([, tables]) => Array.isArray(tables) && tables.some((table) => Array.isArray(table?.rows) && table.rows.length > 0))
      .map(([level]) => level);

    if (levelsWithRows.length) {
      return levelsWithRows;
    }

    if (isCarMode && Array.isArray(state.tables) && state.tables.length) {
      return [state.selectedLevel || NO_LEVEL_KEY];
    }

    return [];
  }, [isCarMode, state.carTablesByLevel, state.selectedLevel, state.tables]);

  useEffect(() => {
    if (!isCarMode || !availableLevels.length) {
      return;
    }

    if (!availableLevels.includes(state.selectedLevel)) {
      updateState({ selectedLevel: availableLevels[0] });
    }
  }, [isCarMode, availableLevels, state.selectedLevel]);

  const deviceInfo = useMemo(
    () => DEVICE_TYPES.find((item) => item.value === state.deviceType),
    [state.deviceType]
  );

  const singlePageDirectoryOptions = useMemo(() => {
    if (!(isStandardMode || isEnterpriseMode)) {
      return [];
    }

    const returnedDirectories = Array.isArray(state.directoryInfo)
      ? state.directoryInfo
          .map((item) => (typeof item === 'string' ? item : (item?.value || item?.label || '')))
          .filter(Boolean)
      : [];

    const sourceDirectories = returnedDirectories.length ? returnedDirectories : PREDEFINED_DIRECTORIES;
    return sourceDirectories.map((dir) => ({ value: dir, label: dir }));
  }, [isStandardMode, isEnterpriseMode, state.directoryInfo]);

  const shouldShowLevelSelector = isCarMode && availableLevels.length > 1;
  const isHistoryDirectoryReplay = state.historyTaskContext?.taskType === 'car_directory';

  const handleDocumentTypeSelect = (type) => {
    resetResultState();
    resetCarFlowState();
    resetDeviceFlowState();
    resetCommon({
      documentType: type,
      currentStep: type === DOCUMENT_TYPE.DEVICE ? STEP.SELECT_DEVICE : STEP.UPLOAD,
      historyTasks: [],
      historyTaskContext: null
    });
  };

  const applyTransformation = (type, options = {}) => {
    updateState({
      transformedTables: processTablesByType(state.tables, type, options),
      currentTransformationType: type
    });
  };

  const getValidatedOffsetAmount = () => {
    const parsed = Number(state.offsetAmount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      updateState({ error: '请输入大于0的增减数量' });
      return null;
    }
    return parsed;
  };

  const handleIncrease = () => {
    const offset = getValidatedOffsetAmount();
    if (offset === null) return;
    updateState({ error: '' });
    applyTransformation('increase', { offset });
  };

  const handleDecrease = () => {
    const offset = getValidatedOffsetAmount();
    if (offset === null) return;
    updateState({ error: '' });
    applyTransformation('decrease', { offset });
  };

  const resetTransformation = () => {
    if (state.documentType === DOCUMENT_TYPE.DEVICE && deviceOriginalTablesRef.current.length) {
      const restoredTables = cloneOriginalTables(deviceOriginalTablesRef.current);
      updateState({
        tables: restoredTables,
        transformedTables: cloneOriginalTables(restoredTables),
        currentTransformationType: 'original'
      });
      return;
    }
    updateState({
      transformedTables: cloneOriginalTables(state.tables),
      currentTransformationType: 'original'
    });
  };

  const handleLoadHistoryTasks = async () => {
    updateState({ historyLoading: true, error: '' });

    try {
      const historyTasks = await loadHistoryTasks({
        isDeviceMode,
        isCarMode,
        documentType: state.documentType,
      });
      updateState({ historyTasks });
    } catch (err) {
      updateState({ error: `历史记录加载失败: ${err.message}` });
    } finally {
      updateState({ historyLoading: false });
    }
  };

  const handleViewHistoryTask = async (taskId) => {
    if (!taskId) return;

    updateState({ historyLoading: true, error: '' });
    try {
      const detail = await fetchHistoryTaskDetail(taskId);
      resetResultState();
      applyHistoryTaskResult({ detail, currentState: state, updateState });
    } catch (err) {
      updateState({ error: `历史详情加载失败: ${err.message}` });
    } finally {
      updateState({ historyLoading: false });
    }
  };

  const handleTableCellValueChange = (cell, value) => {
    updateState({
      tables: applyCellEditToTables(state.tables, cell, value),
      transformedTables: applyCellEditToTables(state.transformedTables, cell, value),
      currentTransformationType: 'original',
      error: ''
    });
  };

  const handleExtractSinglePage = async () => {
    if (!state.file) {
      updateState({ error: '请先选择文件' });
      return;
    }
    if (!state.selectedSinglePageDirectory) {
      updateState({ error: '请选择测试项目' });
      return;
    }

    updateState({ loading: true, error: '' });
    const formData = new FormData();
    formData.append('file', state.file);
    formData.append('directory', state.selectedSinglePageDirectory);
    formData.append('document_type', state.documentType);

    try {
      const { response, data } = await postFormData('/api/car-extract-single-page', formData);
      if (!(response.ok && data?.success)) {
        updateState({ error: data?.error || '单页识别失败', currentStep: STEP.UPLOAD, loading: false });
        return;
      }

      const extractedHtml = data.html || '';
      if (!extractedHtml) {
        updateState({ error: '单页识别成功，但未提取到表格HTML', currentStep: STEP.UPLOAD, loading: false });
        return;
      }

      updateState({
        tableHtml: extractedHtml,
        selectedDirectory: state.selectedSinglePageDirectory,
      });

      await handleExtractTable(
        extractedHtml,
        state.selectedLevel,
        state.documentType,
        (loading) => updateState({ loading }),
        (error) => updateState({ error }),
        (tables) => updateState({ tables, currentStep: STEP.RESULTS }),
        () => {},
        (carTablesByLevel) => updateState({ carTablesByLevel }),
        (selectedLevel) => updateState({ selectedLevel }),
        false,
          false,
        data.task_id || null
      );
    } catch (err) {
      updateState({ error: `网络错误: ${err.message}`, currentStep: STEP.UPLOAD });
    } finally {
      updateState({ loading: false });
    }
  };

  const handleProcessUpload = async () => {
    resetResultState();
    updateState({
      carTablesByLevel: {},
      availableLevels: [],
      currentStep: STEP.UPLOAD,
      error: '',
      historyTaskContext: null
    });
    if (isDeviceMode) {
      const deviceFiles = Array.isArray(state.deviceFiles) ? state.deviceFiles : [];
      const singleDeviceFile = state.file || deviceFiles[0] || null;
      if (deviceFiles.length > 1) {
        await handleProcessDeviceCalibrationBatch(
          deviceFiles,
          state.deviceType,
          (loading) => updateState({ loading }),
          (error) => updateState({ error }),
          (tables) => updateState({ tables }),
          (step) => updateState({ currentStep: step })
        );
        return;
      }
      await handleProcessDeviceCalibration(
        singleDeviceFile,
        state.deviceType,
        (loading) => updateState({ loading }),
        (error) => updateState({ error }),
        (tables) => updateState({ tables }),
        (step) => updateState({ currentStep: step }),
        (selectedDirectory) => updateState({ selectedDirectory }),
        (tableHtml) => updateState({ tableHtml })
      );
      return;
    }

    if (state.uploadMode === UPLOAD_MODE.SINGLE) {
      await handleExtractSinglePage();
      return;
    }

    await handleExtractDirectory(
      state.file,
      state.documentType,
      (loading) => updateState({ loading }),
      (error) => updateState({ error }),
      (directoryInfo) => updateState({ directoryInfo }),
      (availableTables) => updateState({ availableTables }),
      (step) => updateState({ currentStep: step }),
      (selectedDirectory) => updateState({ selectedDirectory }),
      (tableHtml) => updateState({ tableHtml })
    );
  };

  const goBackFromUpload = () => {
    if (isDeviceMode) {
      resetCommon({ currentStep: STEP.SELECT_DEVICE, historyTasks: [], historyTaskContext: null });
      return;
    }
    resetCarFlowState();
    resetCommon({ documentType: '', currentStep: STEP.SELECT_TYPE, historyTasks: [], historyTaskContext: null });
  };

  const handleConfigAction = (action) => {
    if (action === 'back') {
      updateState({
        currentStep: isSingleCarMode ? STEP.UPLOAD : STEP.SELECT_DIRECTORY,
        tableHtml: '',
        selectedDirectory: ''
      });
      return;
    }

    updateState({ currentStep: STEP.RESULTS, loading: true });
    const shouldRecordTableTask = isSingleCarMode && !isHistoryDirectoryReplay;
    handleExtractTable(
      state.tableHtml,
      state.selectedLevel,
      state.documentType,
      (loading) => updateState({ loading }),
      (error) => updateState({ error }),
      (tables) => updateState({ tables, currentStep: STEP.RESULTS }),
      () => {},
      (carTablesByLevel) => updateState({ carTablesByLevel }),
      (selectedLevel) => updateState({ selectedLevel }),
      true,
      shouldRecordTableTask,
      null
    );
  };

    const handleExtractFromDirectory = () => {
    if (!state.tableHtml) {
      updateState({ error: '请选择目录以获取表格' });
      return;
    }

    updateState({ currentStep: STEP.RESULTS, loading: true });
    handleExtractTable(
      state.tableHtml,
      state.selectedLevel,
      state.documentType,
      (loading) => updateState({ loading }),
      (error) => updateState({ error }),
      (tables) => updateState({ tables, currentStep: STEP.RESULTS }),
      () => {},
      (carTablesByLevel) => updateState({ carTablesByLevel }),
      (selectedLevel) => updateState({ selectedLevel }),
      true,
      false,
      null
    );
  };

  const resetToTypeSelect = () => resetCommon({ documentType: '', currentStep: STEP.SELECT_TYPE, historyTasks: [], historyTaskContext: null });

  const resetProcessing = () => {
    resetResultState();
    updateState({ carTablesByLevel: {}, availableLevels: [], historyTaskContext: null });
    resetCommon({ currentStep: STEP.UPLOAD });
  };

  return (
    <div className="page-shell">
      <header className="top-header">
        <div className="top-header-left">
          <img
            src="/tslog-dark.png"
            alt="TS+ Logo"
            className="top-header-logo-left"
            onError={(event) => { event.currentTarget.style.display = 'none'; }}
          />
          <h1 className="top-header-title">EMI限值及计量证书系数 数智化提取系统</h1>
        </div>
        <div className="top-header-right">
          <img
            src="/logo2.png"
            alt="TONSCEND Logo"
            className="top-header-logo-right"
            onError={(event) => { event.currentTarget.style.display = 'none'; }}
          />
        </div>
      </header>

      <div className="app-container">
        {state.error && <div className="error-message"><p>{state.error}</p></div>}
        {renderAppStep({
          availableTables: state.availableTables,
          currentStep: state.currentStep,
          deviceInfo,
          deviceType: state.deviceType,
          directoryInfo: state.directoryInfo,
          documentType: state.documentType,
          file: state.file,
          deviceFiles: state.deviceFiles,
          historyLoading: state.historyLoading,
          historyTasks: state.historyTasks,
          isCarMode,
          isDeviceMode,
          isEnterpriseMode,
          isSingleCarMode,
          isStandardMode,
          loading: state.loading,
          onApplyAbsolute: () => applyTransformation('absolute'),
          onApplyDecrease: handleDecrease,
          onApplyIncrease: handleIncrease,
          onApplyNegative: () => applyTransformation('negative'),
          onApplyOpposite: () => applyTransformation('opposite'),
          onCellValueChange: handleTableCellValueChange,
          onConfirmConfig: handleConfigAction,
          onGoBackFromUpload: goBackFromUpload,
          onLoadHistory: handleLoadHistoryTasks,
          onOffsetAmountChange: (offsetAmount) => updateState({ offsetAmount }),
          onUpdateState: updateState,
          onProcessUpload: handleProcessUpload,
          onExtractFromDirectory: handleExtractFromDirectory,
          onResetProcessing: resetProcessing,
          onResetToTypeSelect: resetToTypeSelect,
          onResetTransformation: resetTransformation,
          onSelectDocumentType: handleDocumentTypeSelect,
          onViewHistory: handleViewHistoryTask,
          selectedDeviceType: state.deviceType,
          selectedDirectory: state.selectedDirectory,
          selectedLevel: state.selectedLevel,
          availableLevels,
          selectedSinglePageDirectory: state.selectedSinglePageDirectory,
          setError: (error) => updateState({ error }),
          setSelectedDirectory: (selectedDirectory) => updateState({ selectedDirectory }),
          setSelectedLevel: (selectedLevel) => updateState({ selectedLevel, error: '' }),
          setTableHtml: (tableHtml) => updateState({ tableHtml }),
          shouldShowLevelSelector,
          singlePageDirectoryOptions,
          state,
          tableHtml: state.tableHtml,
          updateState,
        })}
      </div>
    </div>
  );
}